import React, {Fragment, useState} from 'react';

import {
    completeVerification,
    createGoogleLocation,
    fetchVerificationMethods,
    findLocationInGoogleAccount,
    initiateVerification
} from "./GoogleListingVerification-api";
import CredentialSelector from "../../admin/credentialManagement/CredentialSelector";

const formatAddress = (address) => {
    return address.addressLines[0] +
        (!!address.addressLines[1]
            ? (address.addressLines[1] + " ")
            : "") +
        ", " + address.locality +
        ", " + address.administrativeArea +
        " " + address.postalCode
}

const verificationMethodNames = {
    "PHONE_CALL": "The business was called.",
    "SMS": "An sms was sent to the business.",
    "EMAIL": "An email was sent to the business.",
    "ADDRESS": "A postcard was mailed to the business."
}

const RequestAdminRightsUrl = ({requestAdminRightsUrl}) => <div className="content-spacer-top">
    <div className="invalid-feedback" style={{"display": "block"}}>
        This location has already been verified by another Google My Business account.
        {" "}Click below to navigate to your Google My Business dashboard to request a transfer of ownership.
    </div>
    <a className="btn btn-sm btn-secondary content-spacer-top" href={requestAdminRightsUrl} target="_blank">Request Transfer</a>
</div>

/**
 * Google listing verification component
 */
export function GoogleListingVerification ({ listingId, maybeActiveGoogleVerification }) {
    const [hidden, setHidden] = useState(true)

    const [activeListingVerification, setActiveListingVerification] = useState(maybeActiveGoogleVerification);

    const [selectedGoogleAccount, setSelectedGoogleAccount] = useState(null);

    const [selectedGoogleLocation, setSelectedGoogleLocation] = useState(null);

    // This is set if the location is already verified in another Google My Business account
    const [requestAdminRightsUrl, setRequestAdminRightsUrl] = useState(
        !!maybeActiveGoogleVerification && !!maybeActiveGoogleVerification.requestAdminRightsUrl
            ? maybeActiveGoogleVerification.requestAdminRightsUrl
            : null
    );

    const [loadingCreateGoogleLocation, setLoadingCreateGoogleLocation] = useState(false);

    const [didCreateGoogleLocation, setDidCreateGoogleLocation] = useState(false);

    const [failedToCreateGoogleLocationErrorCode, setFailedToCreateGoogleLocationErrorCode] = useState(null);

    const [availableVerificationMethods, setAvailableVerificationMethods] = useState(null);

    const [selectedVerificationMethod, setSelectedVerificationMethod] = useState(null);

    const [failedToVerifyError, setFailedToVerifyError] = useState(null);

    const [pin, setPin] = useState("");

    const [verifiedSuccessfully, setVerifiedSuccessfully] = useState(false);

    const toggleHidden = () => {
        const newHidden = !hidden;
        setHidden(newHidden);
    }

    const selectGoogleAccount = (credential) => {
        findLocationInGoogleAccount(credential._id, listingId)
            .then(response => {
                const maybeExistingGoogleLocation = response.googleLocation;
                const maybeRequestAdminRightsUrl = response.requestAdminRightsUrl;
                setFailedToCreateGoogleLocationErrorCode(null);
                setRequestAdminRightsUrl(maybeRequestAdminRightsUrl);
                if (!!maybeExistingGoogleLocation) {
                    fetchVerificationMethods(credential._id, maybeExistingGoogleLocation.name)
                        .then(verificationMethods => {
                            setRequestAdminRightsUrl(null);
                            setAvailableVerificationMethods(verificationMethods);
                            setSelectedGoogleLocation(maybeExistingGoogleLocation)
                            setSelectedGoogleAccount(credential)
                        })
                } else {
                    setSelectedGoogleAccount(credential);
                    setSelectedGoogleLocation(null);
                    setFailedToCreateGoogleLocationErrorCode(null);
                    setSelectedVerificationMethod(null);
                    setAvailableVerificationMethods(null);
                    setFailedToVerifyError(null);
                }
            })
    }

    const createNewGoogleLocation = (credentialId, listingId) => {

        setLoadingCreateGoogleLocation(true);
        createGoogleLocation(credentialId, listingId)
            .then(googleLocation => {
                fetchVerificationMethods(selectedGoogleAccount._id, googleLocation.name)
                    .then(verificationMethods => {
                        setLoadingCreateGoogleLocation(false);
                        setAvailableVerificationMethods(verificationMethods);
                        setSelectedGoogleLocation(googleLocation);
                        setDidCreateGoogleLocation(true);
                    })
            })
            .catch(e => {
                // Users can provide this error id to support so we can handle edge cases.
                setFailedToCreateGoogleLocationErrorCode(e.response.data.errorId);
            })
    }

    const setGoogleVerificationMethod = (event) => {
        setSelectedVerificationMethod(event.target.value);
    }

    const sendPin = () => {
        initiateVerification(selectedGoogleAccount._id, listingId, selectedGoogleLocation.name, selectedVerificationMethod)
            .then(verification => {
                setActiveListingVerification(verification);
            })
    }

    const enterPin = () => {
        completeVerification(activeListingVerification._id["$oid"], pin)
            .catch(e => {
                if (e.response.status === 400) {
                    // Expected errors like invalid pin number
                    setFailedToVerifyError(e.response.data.error)
                } else {
                    // Unhandled errors
                    const error = <Fragment>Something went wrong while we attempted to verify the listing using the pin you entered.
                        Please contact support and provide them with the following error code: <b>{e.response.data.errorId}</b>.</Fragment>

                    // Users can provide this error id to support so we can handle edge cases.
                    setFailedToVerifyError(error);
                }
            })
    }

    const maybePhoneCallVerificationMethod = !!availableVerificationMethods && availableVerificationMethods.find(verificationMethod => verificationMethod.verificationMethod === "PHONE_CALL")
    const maybeSmsVerificationMethod = !!availableVerificationMethods && availableVerificationMethods.find(verificationMethod => verificationMethod.verificationMethod === "SMS")
    const maybeEmailVerificationMethod = !!availableVerificationMethods && availableVerificationMethods.find(verificationMethod => verificationMethod.verificationMethod === "EMAIL")
    const maybeAddressVerificationMethod = !!availableVerificationMethods && availableVerificationMethods.find(verificationMethod => verificationMethod.verificationMethod === "ADDRESS")

    const reset = () => {
        setSelectedGoogleAccount(null);
        setRequestAdminRightsUrl(null);
        setDidCreateGoogleLocation(false);
        setFailedToCreateGoogleLocationErrorCode(null);
        setSelectedVerificationMethod(null);
        setAvailableVerificationMethods(null);
        setFailedToVerifyError(null);
    }

    const alreadyVerified = !!activeListingVerification && !!activeListingVerification.googleVerification && activeListingVerification.googleVerification.state === "COMPLETED"

    return (
        <React.Fragment>
            {!alreadyVerified && <Fragment>
            <button className={"btn btn-sm d-inline-flex align-items-center content-spacer-top" + (hidden ? " btn-secondary" : " btn-outline-secondary")} onClick={toggleHidden}>
                {!!requestAdminRightsUrl ? "Request an Ownership Transfer": (!!activeListingVerification ? "Complete Verification" : "Verify Listing")}
            </button>
            {hidden ? <Fragment /> : (!activeListingVerification ? <div className="content-spacer-top">
                {!requestAdminRightsUrl && <div>
                    <b>Google User</b><br />
                    Select a Google Account to assign this verification request.<br />
                    To manage Google Accounts, visit External Logins in My Account.<br />
                    <CredentialSelector
                        selected={selectedGoogleAccount}
                        provider={["GOOGLEMAP"]}
                        placeholder="Select Google Account"
                        onSelected={(credential) => {
                            selectGoogleAccount(credential)
                        }}
                    />
                </div>}
                {!!selectedGoogleAccount && <div>
                    {/*{!!selectedGoogleLocation && <div>*/}
                    {/*    {didCreateGoogleLocation ? "We added this location to your Google Account."*/}
                    {/*        : "We found this location in your Google Account."} Would you like to verify this location?<br />*/}
                    {/*    {selectedGoogleLocation.locationName} {formatAddress(selectedGoogleLocation.address)}*/}
                    {/*</div>}*/}
                    {!selectedGoogleLocation && !failedToCreateGoogleLocationErrorCode && !requestAdminRightsUrl && <div>
                        We were unable to find that location in your Google My Business Account. Would you like to add it?<br />
                        <button disabled={loadingCreateGoogleLocation} className="btn btn-sm btn-secondary content-spacer-top" onClick={() => createNewGoogleLocation(selectedGoogleAccount._id, listingId)}>Add Location</button>
                    </div>}
                    {!!requestAdminRightsUrl && <RequestAdminRightsUrl requestAdminRightsUrl={requestAdminRightsUrl} />}
                    {!!failedToCreateGoogleLocationErrorCode && <div className="invalid-feedback" style={{"display": "block"}}>
                        Something went wrong while we attempted to add this location to your Google Account.
                         Please contact support and provide them with the following error code: <b>{failedToCreateGoogleLocationErrorCode}</b>.
                         You may also try adding this location to another Google Account.</div>}
                    {!!availableVerificationMethods && <div className="content-spacer-top">
                        <b className="content-spacer-top">Verification Method</b><br />
                        In order to fully manage and represent your business on Google, you will need to confirm you are associated with it.<br />
                        Select from the following options:
                        <form>
                            {!!maybePhoneCallVerificationMethod && <div><label><input type="radio" name="googleVerificationMethod" onClick={setGoogleVerificationMethod} value={"PHONE_CALL"} /> Phone Call to {maybePhoneCallVerificationMethod.phoneData.phoneNumber}</label></div>}
                            {!!maybeSmsVerificationMethod && <div><label><input type="radio" name="googleVerificationMethod" onClick={setGoogleVerificationMethod} value={"SMS"} /> SMS to {maybeSmsVerificationMethod.phoneData.phoneNumber}</label></div>}
                            {!!maybeAddressVerificationMethod && <div>
                                <label><input
                                    type="radio"
                                    name="googleVerificationMethod"
                                    onClick={setGoogleVerificationMethod}
                                    value={"ADDRESS"} /> Mail postcard to {maybeAddressVerificationMethod.addressData.businessName} {formatAddress(maybeAddressVerificationMethod.addressData.address)}
                                </label>
                            </div>}
                            {!!maybeEmailVerificationMethod && <div>
                                <label><input
                                    type="radio"
                                    name="googleVerificationMethod"
                                    onClick={setGoogleVerificationMethod}
                                    value={"EMAIL"} /> Email to {maybeEmailVerificationMethod.emailData.userName}@{maybeEmailVerificationMethod.emailData.domainName}
                                </label>
                            </div>}
                        </form>
                        <button className="btn btn-sm btn-outline-secondary content-spacer-top" onClick={reset}>Cancel</button>
                        <button className="btn btn-sm btn-secondary control-spacer-left content-spacer-top" disabled={!selectedVerificationMethod} onClick={sendPin} value={pin}>Send Code</button>
                    </div>}
                </div>}
            </div> : <Fragment>
                {verifiedSuccessfully && <div>The location has been verified.</div>}
                {!verifiedSuccessfully && !!activeListingVerification.requestAdminRightsUrl && <RequestAdminRightsUrl requestAdminRightsUrl={activeListingVerification.requestAdminRightsUrl} />}
                {!verifiedSuccessfully && !activeListingVerification.requestAdminRightsUrl && <div>
                    <b className="content-spacer-top">Google User</b><br />
                    {activeListingVerification.googleAccountName}<br/><br />

                    <b className="content-spacer-top">Date Submitted</b><br />
                    {new Date(activeListingVerification.googleVerification.createTime["$date"]).toLocaleDateString()}<br/><br />

                    <b className="content-spacer-top">Verification Method</b><br />
                    {verificationMethodNames[activeListingVerification.googleVerification.method]}<br/><br />

                    <input type="text" placeholder="Enter Pin" className="form-control text-input" onChange={e => setPin(e.target.value)} /><br />
                    <button className="btn btn-secondary btn-sm d-inline-flex align-items-center content-spacer-top" onClick={enterPin}>Submit</button>
                    {!!failedToVerifyError && <div className="invalid-feedback" style={{"display": "block"}}>
                        {failedToVerifyError}
                    </div>}
                </div>}
            </Fragment>)}
            </Fragment>}
        </React.Fragment>
    );
}
